import { isGlobalRole, type GlobalRole } from '@opsflow/api-contracts';
import { lazy, Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SignInPage } from './features/auth/sign-in/sign-in-page';
import * as m from './paraglide/messages.js';

const AdminWorkspace = lazy(() => import('./app/router'));
// Reached from a mail client by people who have no session yet, so they are resolved before
// `/api/me` is ever asked: an invitation recipient is anonymous by definition, and landing them
// on the sign-in screen would strand them with a link they cannot use.
const PasswordChoicePage = lazy(() =>
  import('./features/users/password-choice-page').then((module) => ({
    default: module.PasswordChoicePage,
  })),
);
const ForgotPasswordPage = lazy(() =>
  import('./features/users/password-sign-in').then((module) => ({
    default: module.ForgotPasswordPage,
  })),
);

/** Path-matched before authentication. Kept off the router, which only mounts once signed in. */
function anonymousRoute(pathname: string) {
  // Paraglide prefixes a localised URL, so the locale segment is dropped before matching.
  const path = pathname.replace(/^\/(en|fr)(?=\/|$)/, '') || '/';
  if (path === '/invitation') return <PasswordChoicePage kind="invitation" />;
  if (path === '/password-reset') return <PasswordChoicePage kind="reset" />;
  if (path === '/forgot-password') return <ForgotPasswordPage />;
  return null;
}

export interface Me {
  personId: string;
  displayName: string;
  globalRole: GlobalRole;
  status: 'ACTIVE' | 'INACTIVE';
  capabilities: readonly string[];
  /** Present only while an administrator is acting as this person. */
  impersonatedBy?: { personId: string; displayName: string };
}

type AuthState = { kind: 'anonymous' } | { kind: 'denied' } | { kind: 'signed-in'; me: Me };

export function App() {
  const anonymous = anonymousRoute(window.location.pathname);
  const identity = useQuery({
    queryKey: ['session'],
    queryFn: ({ signal }) => loadIdentity(signal),
    enabled: !anonymous,
    staleTime: Infinity,
    retry: false,
    networkMode: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  if (anonymous) {
    return (
      <Suspense
        fallback={<main className="p-8 text-sm text-muted-foreground">{m.app_loading()}</main>}
      >
        {anonymous}
      </Suspense>
    );
  }

  if (!identity.data) {
    return <main className="p-8 text-sm text-muted-foreground">{m.app_loading()}</main>;
  }
  const state = identity.data;

  if (state.kind === 'anonymous') {
    return <SignInPage />;
  }

  if (state.kind === 'denied') {
    return (
      <main className="mx-auto max-w-lg p-8">
        <p role="alert">{m.app_access_unavailable()}</p>
      </main>
    );
  }

  return (
    <Suspense
      fallback={
        <main className="p-8" role="status">
          {m.app_opening_workspace()}
        </main>
      }
    >
      <AdminWorkspace key={`${state.me.personId}:${state.me.globalRole}`} me={state.me} />
    </Suspense>
  );
}

async function loadIdentity(signal: AbortSignal): Promise<AuthState> {
  try {
    const response = await fetch('/api/me', { credentials: 'same-origin', signal });
    if (!response.ok) return { kind: 'anonymous' };
    const body = (await response.json()) as { success: boolean; data: unknown };
    return body.success && isActiveIdentity(body.data)
      ? { kind: 'signed-in', me: body.data }
      : { kind: 'denied' };
  } catch {
    return { kind: 'anonymous' };
  }
}

function isActiveIdentity(value: unknown): value is Me {
  if (typeof value !== 'object' || value === null) return false;
  const me = value as Record<string, unknown>;
  return (
    typeof me.personId === 'string' &&
    typeof me.displayName === 'string' &&
    me.status === 'ACTIVE' &&
    Array.isArray(me.capabilities) &&
    me.capabilities.every((capability: unknown) => typeof capability === 'string') &&
    isGlobalRole(me.globalRole)
  );
}
